import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Archive,
  Reply,
  Forward,
  MoreVertical,
  User,
  Paperclip,
  Download,
  FileText,
  Mail as MailIcon,
  Clock,
  Trash2,
  ChevronDown,
  ChevronUp,
  Send,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, MailAttachment, MailThread } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { EmailResumeParser } from "./EmailResumeParser";

interface MailDisplayProps {
  mail: Mail | null;
  thread?: MailThread | null;
  onReply: (mail: Mail) => void;
  onArchive: (mailId: string) => void;
  onDelete?: (mailId: string) => void;
  isSentFolder?: boolean;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
}

function getDisplayName(mail: Mail, isSent: boolean = false): string {
  if (isSent) {
    return mail.recipient_email || mail.to_email || "Unknown";
  }
  if (mail.candidates) {
    const name = [mail.candidates.first_name, mail.candidates.last_name]
      .filter(Boolean)
      .join(" ");
    if (name) return name;
  }
  if (mail.from_name) return mail.from_name;
  return mail.from_email.split("<")[0].trim() || mail.from_email;
}

function getEmailAddress(mail: Mail, isSent: boolean = false): string {
  if (isSent) {
    return mail.recipient_email || mail.to_email || "";
  }
  const match = mail.from_email.match(/<(.+)>$/);
  return match ? match[1] : mail.from_email;
}

function SingleMailView({ 
  mail, 
  onReply, 
  onArchive, 
  onDelete,
  isSent = false,
  isCollapsible = false,
  defaultOpen = true,
}: { 
  mail: Mail; 
  onReply: (mail: Mail) => void;
  onArchive: (mailId: string) => void;
  onDelete?: (mailId: string) => void;
  isSent?: boolean;
  isCollapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const downloadAttachment = async (attachment: MailAttachment) => {
    try {
      const { data, error } = await supabase.storage
        .from("inbound-email-attachments")
        .download(attachment.storage_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  };

  const displayName = getDisplayName(mail, isSent);
  const emailAddress = getEmailAddress(mail, isSent);

  const header = (
    <div className={cn(
      "flex items-center gap-4 p-4",
      isCollapsible && "cursor-pointer hover:bg-muted/50"
    )}>
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{displayName}</span>
          {isSent && (
            <Badge variant="secondary" className="text-xs shrink-0">
              <Send className="h-3 w-3 mr-1" />
              Sent
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">{emailAddress}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-muted-foreground">
          {format(new Date(mail.sent_at || mail.received_at), "MMM d, h:mm a")}
        </span>
        {isCollapsible && (
          isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </div>
  );

  const attachments = mail.inbound_email_attachments || [];
  
  const content = (
    <div className="px-4 pb-4 space-y-4">
      {/* Attachments */}
      {attachments.length > 0 && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments ({attachments.length})
            </h4>
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 px-3"
                    onClick={() => downloadAttachment(attachment)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    <div className="text-left">
                      <div className="text-sm truncate max-w-[180px]">
                        {attachment.file_name}
                      </div>
                      {attachment.file_size && (
                        <div className="text-xs text-muted-foreground">
                          {(attachment.file_size / 1024).toFixed(1)} KB
                        </div>
                      )}
                    </div>
                    <Download className="h-3 w-3 ml-2" />
                  </Button>
                  <EmailResumeParser
                    attachment={attachment}
                    emailFromName={mail.from_name}
                    emailFromEmail={mail.from_email.match(/<(.+)>$/)?.[1] || mail.from_email}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Email Body */}
      {mail.body_html ? (
        <div
          className="prose prose-sm max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: mail.body_html }}
        />
      ) : (
        <pre className="whitespace-pre-wrap font-sans text-sm">
          {mail.body_text || "No content"}
        </pre>
      )}
    </div>
  );

  if (isCollapsible) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-b">
        <CollapsibleTrigger asChild>
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent>
          {content}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="border-b">
      {header}
      {content}
    </div>
  );
}

export function MailDisplay({ 
  mail, 
  thread, 
  onReply, 
  onArchive, 
  onDelete,
  isSentFolder = false 
}: MailDisplayProps) {
  const navigate = useNavigate();

  if (!mail && !thread) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <MailIcon className="h-16 w-16 mb-4 opacity-30" />
        <p className="text-lg">Select an email to read</p>
        <p className="text-sm mt-1">Choose from the list on the left</p>
        <p className="text-xs mt-4 text-muted-foreground/70">
          Tip: Use j/k or ↑/↓ to navigate, r to reply, e to archive
        </p>
      </div>
    );
  }

  const displayMail = mail || thread?.latestMail;
  if (!displayMail) return null;

  const displayName = getDisplayName(displayMail, isSentFolder);

  return (
    <div className="flex h-full flex-col">
      {/* Header with actions */}
      <div className="flex items-center justify-between p-4 border-b gap-2">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold truncate">
            {displayMail.subject || "(No Subject)"}
          </h2>
          {thread && thread.totalCount > 1 && (
            <p className="text-sm text-muted-foreground">
              {thread.totalCount} messages in this conversation
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {displayMail.candidate_id && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    navigate(`/recruiter/candidates?id=${displayMail.candidate_id}`)
                  }
                >
                  <User className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Candidate</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => onReply(displayMail)}>
                <Reply className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reply (r)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onArchive(displayMail.id)}
              >
                <Archive className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Archive (e)</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onReply(displayMail)}>
                <Reply className="h-4 w-4 mr-2" />
                Reply
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onArchive(displayMail.id)}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(displayMail.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content - Thread or Single Email */}
      <ScrollArea className="flex-1">
        {thread && thread.totalCount > 1 ? (
          // Thread view - show all emails in conversation
          <div className="divide-y">
            {thread.mails.slice().reverse().map((threadMail, index) => (
              <SingleMailView
                key={threadMail.id}
                mail={threadMail}
                onReply={onReply}
                onArchive={onArchive}
                onDelete={onDelete}
                isSent={threadMail.direction === "outbound" || isSentFolder}
                isCollapsible={thread.mails.length > 1}
                defaultOpen={index === thread.mails.length - 1}
              />
            ))}
          </div>
        ) : (
          // Single email view
          <SingleMailView
            mail={displayMail}
            onReply={onReply}
            onArchive={onArchive}
            onDelete={onDelete}
            isSent={isSentFolder}
          />
        )}
      </ScrollArea>
    </div>
  );
}
